import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  apiRequest,
  errorFormatHandler,
  isDevelop,
} from "../../helpers/apiHelper";

const apiEnv = isDevelop
  ? process.env.REACT_APP_TEST_API_URL
  : process.env.REACT_APP_PUBLIC_API_URL;

export const fetchUsers = createAsyncThunk(
  "/admin/users",
  async (
    params: {
      page: number;
      perPage: number;
      username?: string;
      firstName?: string;
      lastName?: string;
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const { data } = await apiRequest(`/showAllUsers`, "GET", params);

      return data;
    } catch (err: any) {
      console.warn("/admin/users", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const fetchUserDetails = createAsyncThunk(
  "/admin/user",
  async (userId: string, { rejectWithValue }) => {
    try {
      const { data } = await apiRequest(`/returnUser/${userId}`, "GET");

      return data;
    } catch (err: any) {
      console.warn("/admin/user", err);
      return rejectWithValue(errorFormatHandler(err));
    }
  },
);

export const postUserDetails = createAsyncThunk(
  "/admin/user",
  async (
    {
      userId,
      userDetails,
    }: {
      userId: string | null;
      userDetails: {
        userName: string;
        firstName: string;
        lastName: string;
        password: string;
      };
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const { data } = await apiRequest(`/register`, "POST", {
        userId,
        ...userDetails,
      });

      return data;
    } catch (err: any) {
      console.warn("/admin/user", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const postDeleteUser = createAsyncThunk(
  "/admin/deleteUser",
  async (userId: string, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await apiRequest(`/deleteUser/${userId}`, "GET");

      return data;
    } catch (err: any) {
      console.warn("/admin/deleteUser", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const fetchUserPermissionsWeb = createAsyncThunk(
  "/admin/fetchUserPermissionsWeb",
  async (userId: string, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await apiRequest(`/ruteWebApp/${userId}`, "GET");

      return data;
    } catch (err: any) {
      console.warn("/admin/fetchUserPermissionsWeb", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const postUserPermissionsWeb = createAsyncThunk(
  "/admin/postUserPermissionsWeb",
  async (
    transferData: {
      userId: string;
      permissions: {
        idRoute: number;
        subRoutes: {
          idRoute: number;
          isChecked: boolean;
        }[];
      }[];
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const { data } = await apiRequest("/addUserRoutes", "POST", transferData);

      return data;
    } catch (err: any) {
      console.warn("/admin/postUserPermissionsWeb", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const fetchUserPermissionsMobile = createAsyncThunk(
  "/admin/fetchUserPermissionsMobile",
  async (userId: string, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await apiRequest(`/ruteMobileApp/${userId}`, "GET");

      return data;
    } catch (err: any) {
      console.warn("/admin/fetchUserPermissionsMobile", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const postUserPermissionsMobile = createAsyncThunk(
  "/admin/postUserPermissionsMobile",
  async (
    transferData: {
      userId: string;
      permissions: {
        idRoute: number;
      }[];
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const { data } = await apiRequest(
        "addUserRoutesMobile",
        "POST",
        transferData,
      );

      return data;
    } catch (err: any) {
      console.warn("/admin/postUserPermissionsMobile", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

interface GetCellsNomenclatureInterface {
  cladire?: string;
  page?: number;
  per_page?: number;
  alee?: string;
  celula?: string;
  coloana?: string;
  insula?: string;
  rand?: string;
  polita?: string;
  zona?: number;
  type?: number;
}

export const getCellsNomenclature = createAsyncThunk(
  "/admin/getCellsNomenclature",
  async (
    filters: GetCellsNomenclatureInterface,
    { rejectWithValue, dispatch },
  ) => {
    const {
      cladire = "",
      page = "",
      per_page = "",
      alee = "",
      celula = "",
      coloana = "",
      insula = "",
      rand = "",
      polita = "",
      zona = "",
      type = "",
    } = filters;

    try {
      const { data } = await apiRequest(
        `/cellsNomenclature?page=${page}&per_page=${per_page}&cladire=${cladire}&alee=${alee}&celula=${celula}&coloana=${coloana}&insula=${insula}&rand=${rand}&polita=${polita}&zona=${zona}&type=${type}`,
        "GET",
      );
      return data;
    } catch (err: any) {
      console.warn("/admin/getCellsNomenclature", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const postCreateOrUpdateCellNomenclature = createAsyncThunk(
  "/admin/postCreateOrUpdateCellNomenclature",
  async (
    {
      params,
      callBack,
    }: {
      params: any;
      callBack?: () => void;
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const { data } = await apiRequest("/createOrUpdateCell", "POST", params);
      callBack && callBack();
      return data;
    } catch (err: any) {
      console.warn("/admin/postCreateOrUpdateCellNomenclature", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const getSelectCellNomenclature = createAsyncThunk(
  "/admin/getSelectCellNomenclature",
  async (idCelula: number | string, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await apiRequest(`/getSelectCell/${idCelula}`, "GET");
      return data;
    } catch (err: any) {
      console.warn("/admin/getSelectCellNomenclature", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const fetchDeleteCellNomenclature = createAsyncThunk(
  "/admin/deleteCellNomenclature",
  async (
    {
      idCelula,
    }: {
      idCelula: string | number;
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const { data } = await apiRequest(`/deleteCell/${idCelula}`, "GET");
      dispatch(getCellsNomenclature({}));
      return data;
    } catch (err: any) {
      console.warn("/admin/deleteCellNomenclature", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const getPrintCellNomenclature = createAsyncThunk(
  "/admin/getPrintCellNomenclature",
  async (
    {
      idCelula,
    }: {
      idCelula: number | string;
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      window.open(`${apiEnv}/printCellBarCode/${idCelula}`, "_blank")?.focus();
    } catch (err: any) {
      console.warn("/admin/getPrintCellNomenclature", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const getDevicesNomenclature = createAsyncThunk(
  "/admin/getDevicesNomenclature",
  async (params: any, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await apiRequest(`/devicesActive`, "GET", params);
      return data;
    } catch (err: any) {
      console.warn("/admin/getDevicesNomenclature", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

type UpdateDevicePayload = {
  idDevice: string | number;
  userId: number | string;
  observatii: string | null;
  file: File;
};

export const postUpdateDevice = createAsyncThunk(
  "/admin/postUpdateDevice",
  async (
    { idDevice, userId, observatii, file }: UpdateDevicePayload,
    { rejectWithValue, dispatch },
  ) => {
    try {
      const fd = new FormData();
      fd.append("userId", String(userId));
      fd.append("observatii", observatii ?? "");
      fd.append("file", file);

      const { data } = await apiRequest(
        `/updateDevice/${idDevice}`,
        "POST",
        fd,
      );
      return data;
    } catch (err: any) {
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const fetchDeleteDevice = createAsyncThunk(
  "/admin/deleteDevice",
  async (
    {
      idDevice,
    }: {
      idDevice: string | number;
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const { data } = await apiRequest(`/deleteDevice/${idDevice}`, "GET");
      dispatch(getDevicesNomenclature({}));
      return data;
    } catch (err: any) {
      console.warn("/admin/deleteDevice", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const postCreateDevice = createAsyncThunk(
  "/admin/postCreateDevice",
  async (
    {
      params,
    }: {
      params: any;
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const { data } = await apiRequest("/createDevice", "POST", params);
      return data;
    } catch (err: any) {
      console.warn("/admin/postCreateDevice", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const deleteOrRestoreDevice = createAsyncThunk(
  "/admin/deleteOrRestoreDevice",
  async (
    {
      idDevice,
      observatii,
      tableContext,
    }: {
      idDevice: string | number;
      observatii?: string | number | null;
      tableContext: any;
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const { data } = await apiRequest(
        `/deleteOrRestoreDevice/${idDevice}`,
        "POST",
        { observatii },
      );
      dispatch(getDevicesNomenclature(tableContext));
      return data;
    } catch (err: any) {
      console.warn("/admin/deleteOrRestoreDevice", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const getDownloadDevice = createAsyncThunk(
  "/admin/getDownloadDevice",
  async (
    {
      idDevice,
    }: {
      idDevice: number | string;
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      window.open(`${apiEnv}/downloadDevice/${idDevice}`, "_blank")?.focus();
    } catch (err: any) {
      console.warn("/admin/getDownloadDevice", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const getDeviceHistory = createAsyncThunk(
  "/admin/getDeviceHistory",
  async (
    {
      sn,
    }: {
      sn: number | string;
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const { data } = await apiRequest(`/getDeviceHistory/${sn}`, "GET");

      return data;
    } catch (err: any) {
      console.warn("/admin/getDeviceHistory", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);
